import type ArrayProxy from '@ember/array/proxy';
import Service, { service } from '@ember/service';
import type Store from '@ember-data/store';
import { task, type TaskInstance } from 'ember-concurrency';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type { RecordArray } from 'garaje/utils/type-utils';

// The endpoint runs an authz check and a role lookup per row, so 100 records measured at "a few
// seconds". First paint is one page. Lower this, not higher — the endpoint accepts up to 1000, so
// its ceiling is not what constrains this number; the per-row cost is.
export const BLOCKLIST_PAGE_SIZE = 50;

/** `meta` optional on purpose — the guard in `queryPage` is load-bearing, not defensive dressing. */
type BlocklistFilterQueryResult = RecordArray<BlacklistFilter> & { meta?: { total?: number } };

export type BlocklistFilterScope =
  | { kind: 'company'; companyId: string; appliedLocationId?: string }
  | { kind: 'property'; propertyId: string };

// Lives here, not beside the component: a type imported from a `.gts` module resolves to `any`
// under the repo-wide type-aware eslint pass, silently un-typing the `.ts` property variant.
export interface BlocklistFilterPageRequest {
  offset: number;
  limit: number;
  search?: string;
  locationView?: 'this' | 'all';
}

export interface BlocklistFilterPage {
  records: BlacklistFilter[];
  /** Count of every matching record, or `null` if absent — which means unknown, NOT "no more". */
  total: number | null;
}

/**
 * The `visitor-profile` linkage serializes only under an explicit include, and a
 * filter created from a profile carries it in the store but loses it on the next
 * refetch. Every consumer of the link (the linked-profile badge, the read-only
 * matched-field gate) therefore depends on this one include, so it is requested
 * where the list is loaded rather than in any single consumer.
 */
function withVisitorProfileInclude(include: string | undefined, featureFlags: FeatureFlagsService): string | undefined {
  if (!featureFlags.isEnabled('vis-blocklist-linked-profile')) return include;
  return include ? `${include},visitor-profile` : 'visitor-profile';
}

export default class BlocklistFilterService extends Service {
  @service declare store: Store;
  @service declare featureFlags: FeatureFlagsService;

  get locationBlocklistFilters(): ArrayProxy<BlacklistFilter> | BlacklistFilter[] {
    return this.queryByCompanyTask.lastSuccessful?.value || [];
  }

  get propertyBlocklistFilters(): ArrayProxy<BlacklistFilter> | BlacklistFilter[] {
    return this.queryByPropertyTask.lastSuccessful?.value || [];
  }

  // legacy API
  query(companyId: string): TaskInstance<ArrayProxy<BlacklistFilter>> {
    return this.queryByCompanyTask.perform(companyId);
  }

  // "This location" passes filter[applied-location] (the BE self-scopes the company, so it is omitted);
  // "All locations" passes filter[company]. Includes are flag-gated because an include the resource
  // doesn't expose raises InvalidInclude → 400 for every company.
  queryByCompanyTask = task(async (companyId: string, appliedLocationId?: string) => {
    const scope: BlocklistFilterScope = { kind: 'company', companyId, appliedLocationId };
    const query: { filter: Record<string, string>; include?: string } = { filter: this.#scopeFilter(scope) };
    // Assigned only when non-empty: an `include: undefined` own key is not the same as no include.
    const include = this.#includeFor(scope);
    if (include) query.include = include;
    return await this.store.query('blacklist-filter', query);
  });

  queryByPropertyTask = task(async (propertyId: string) => {
    const scope: BlocklistFilterScope = { kind: 'property', propertyId };
    const query: { filter: Record<string, string>; include?: string } = { filter: this.#scopeFilter(scope) };
    const include = this.#includeFor(scope);
    if (include) query.include = include;
    return await this.store.query('blacklist-filter', query);
  });

  /**
   * Sending `page` is what opts into server-side pagination; the callers above omit it and still get
   * the whole capped collection. Not a task — a shared task instance would drop a concurrent load.
   */
  async queryPage(
    scope: BlocklistFilterScope,
    { offset, limit = BLOCKLIST_PAGE_SIZE, search }: { offset: number; limit?: number; search?: string },
  ): Promise<BlocklistFilterPage> {
    const filter = this.#scopeFilter(scope);
    // Trimmed: the term is interpolated into `%...%` server-side, so " smith " would look for a
    // literal leading space and match nothing. All-whitespace collapses to no filter at all.
    const term = search?.trim();
    if (term) filter['search'] = term;

    const query: {
      filter: Record<string, string>;
      page: { offset: number; limit: number };
      include?: string;
    } = { filter, page: { offset, limit } };
    const include = this.#includeFor(scope);
    if (include) query.include = include;

    const result = <BlocklistFilterQueryResult>await this.store.query('blacklist-filter', query);
    const total = result.meta?.total;

    return { records: result.slice(), total: typeof total === 'number' ? total : null };
  }

  // Shared so the paginated and unpaginated paths can't disagree on filters or includes.
  #scopeFilter(scope: BlocklistFilterScope): Record<string, string> {
    if (scope.kind === 'property') return { property: scope.propertyId };
    return scope.appliedLocationId ? { 'applied-location': scope.appliedLocationId } : { company: scope.companyId };
  }

  #includeFor(scope: BlocklistFilterScope): string | undefined {
    const base =
      scope.kind === 'company' && this.featureFlags.isEnabled('feat-blocklist-location-scoping')
        ? 'company,locations'
        : undefined;
    return withVisitorProfileInclude(base, this.featureFlags);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    blocklistFilter: BlocklistFilterService;
  }
}
